import { useState, useMemo, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Download, FileText, Heart, Clock, DollarSign, Shield, 
  Calendar, Play, CheckCircle2, Users, Loader2
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { exportToCSV } from "@/lib/exportUtils";
import { formatDateDisplay } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

interface PersonalReport {
  id: string;
  name: string;
  description: string;
  category: "benefits" | "time" | "personal";
  icon: any;
}

const personalReports: PersonalReport[] = [
  {
    id: "benefits-summary",
    name: "My Benefits Summary",
    description: "Overview of all your enrolled benefits and coverage details",
    category: "benefits",
    icon: Heart,
  },
  {
    id: "coverage-details",
    name: "Coverage Details",
    description: "Detailed breakdown of your plan coverage, deductibles, and costs",
    category: "benefits",
    icon: Shield,
  },
  {
    id: "dependents-coverage",
    name: "Dependents Coverage",
    description: "Benefits coverage for your enrolled dependents",
    category: "benefits",
    icon: Users,
  },
  {
    id: "premium-contributions",
    name: "Premium Contributions",
    description: "Your payroll deductions for benefits premiums",
    category: "benefits",
    icon: DollarSign,
  },
  {
    id: "pto-balance",
    name: "PTO Balance Report",
    description: "Current time off balances by category",
    category: "time",
    icon: Clock,
  },
  {
    id: "pto-history",
    name: "Time Off History",
    description: "Complete history of your time off requests and usage",
    category: "time",
    icon: Calendar,
  },
  {
    id: "employment-summary",
    name: "Employment Summary",
    description: "Your employment details, position, and tenure",
    category: "personal",
    icon: FileText,
  },
];

const categoryColors: Record<string, { bg: string; text: string; icon: string }> = {
  benefits: { bg: "bg-pink-100 dark:bg-pink-900/30", text: "text-pink-700 dark:text-pink-300", icon: "text-pink-500" },
  time: { bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-300", icon: "text-blue-500" },
  personal: { bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-300", icon: "text-green-500" },
};

export default function Reports() {
  const { user } = useAuth();
  const { toast } = useToast();
  const reportPreviewRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState("all");
  const [runningReportId, setRunningReportId] = useState<string | null>(null);
  const [reportData, setReportData] = useState<any[]>([]);
  const [showReportPreview, setShowReportPreview] = useState(false);
  const [currentReport, setCurrentReport] = useState<PersonalReport | null>(null);

  const { data: employee, isLoading: employeeLoading } = useQuery<any>({
    queryKey: ["/api/employees/me"],
  });

  const { data: enrollments = [], isLoading: enrollmentsLoading } = useQuery<any[]>({
    queryKey: ["/api/employees", user?.employeeId, "enrollments"],
    enabled: Boolean(user?.employeeId),
    queryFn: async () => {
      const res = await fetch(`/api/employees/${user?.employeeId}/enrollments`);
      if (!res.ok) return [];
      return res.json();
    },
  });

  const { data: dependents = [], isLoading: dependentsLoading } = useQuery<any[]>({
    queryKey: ["/api/employees", user?.employeeId, "dependents"],
    enabled: Boolean(user?.employeeId),
    queryFn: async () => {
      const res = await fetch(`/api/employees/${user?.employeeId}/dependents`);
      if (!res.ok) return [];
      return res.json();
    },
  });

  const { data: rawPtoBalances, isLoading: balancesLoading } = useQuery<any>({
    queryKey: ["/api/time-off/employee/balances"],
  });

  const { data: ptoRequests = [], isLoading: requestsLoading } = useQuery<any[]>({
    queryKey: ["/api/time-off/employee/requests"],
  });

  const { data: plans = [] } = useQuery<any[]>({
    queryKey: ["/api/plans"],
  });

  const planMap = useMemo(() => {
    const m = new Map<number, any>();
    (plans as any[]).forEach((p) => m.set(p.id, p));
    return m;
  }, [plans]);

  const isDataLoading =
    employeeLoading || enrollmentsLoading || dependentsLoading || balancesLoading || requestsLoading;

  const myEnrollments = enrollments;
  const myDependents = dependents;

  const filteredReports = useMemo(() => {
    if (activeTab === "all") return personalReports;
    return personalReports.filter(r => r.category === activeTab);
  }, [activeTab]);

  const runReport = async (report: PersonalReport) => {
    setRunningReportId(report.id);
    setCurrentReport(report);

    let data: any[] = [];
    
    switch (report.id) {
      case "benefits-summary":
        data = myEnrollments.map((e: any) => {
          const plan = planMap.get(e.planId);
          return {
            "Plan Type": plan?.type || "N/A",
            "Plan Name": plan?.name || "N/A",
            "Coverage Level": e.coverageTier || "N/A",
            "Status": e.status || "N/A",
            "Effective Date": e.effectiveDate ? formatDateDisplay(e.effectiveDate) : "N/A",
            "Monthly Premium": e.totalPremium ? `$${Number(e.totalPremium).toFixed(2)}` : "N/A",
            "Employee Cost": e.employeePremium ? `$${Number(e.employeePremium).toFixed(2)}` : "N/A",
          };
        });
        break;

      case "coverage-details":
        data = myEnrollments.map((e: any) => {
          const plan = planMap.get(e.planId);
          return {
            "Plan Type": plan?.type || "N/A",
            "Plan Name": plan?.name || "N/A",
            "Coverage Level": e.coverageTier || "N/A",
            "Deductible": plan?.deductible || "N/A",
            "Out of Pocket Max": plan?.outOfPocketMax || "N/A",
            "Copay": plan?.copay || "N/A",
            "Network": plan?.networkType || "N/A",
          };
        });
        break;
        
      case "dependents-coverage":
        data = myDependents.map((d: any) => ({
          "Name": `${d.firstName} ${d.lastName}`,
          "Relationship": d.relationship || "N/A",
          "Date of Birth": d.dateOfBirth ? formatDateDisplay(d.dateOfBirth) : "N/A",
          "SSN Last 4": d.ssn ? `***-**-${d.ssn.slice(-4)}` : "N/A",
          "Status": d.status || "Active",
        }));
        if (data.length === 0) {
          data = [{ "Note": "No dependents enrolled" }];
        }
        break;
        
      case "premium-contributions":
        data = myEnrollments.filter((e: any) => e.status === "active").map((e: any) => {
          const plan = planMap.get(e.planId);
          return {
            "Benefit Type": plan?.type || "N/A",
            "Plan Name": plan?.name || "N/A",
            "Total Premium": e.totalPremium ? `$${Number(e.totalPremium).toFixed(2)}` : "$0.00",
            "Employer Contribution": e.employerPremium ? `$${Number(e.employerPremium).toFixed(2)}` : "$0.00",
            "Your Cost (Monthly)": e.employeePremium ? `$${Number(e.employeePremium).toFixed(2)}` : "$0.00",
            "Your Cost (Per Pay Period)": e.payrollDeduction ? `$${Number(e.payrollDeduction).toFixed(2)}` : "N/A",
          };
        });
        break;
        
      case "pto-balance":
        if (Array.isArray(rawPtoBalances) && rawPtoBalances.length > 0) {
          data = rawPtoBalances.map((b: any) => ({
            "Type": b.policyName || b.policyType || "Unknown",
            "Allocated": b.allocated || 0,
            "Used": b.used || 0,
            "Pending": b.pending || 0,
            "Available": b.available || 0,
            "Unit": b.accrualUnit || "hours",
          }));
        } else {
          data = [{ "Note": "No PTO balance data available" }];
        }
        break;
        
      case "pto-history":
        data = ptoRequests.map((r: any) => ({
          "Request Date": r.submittedDate ? formatDateDisplay(r.submittedDate) : "N/A",
          "Type": r.policyName || r.type || "N/A",
          "Start Date": r.startDate ? formatDateDisplay(r.startDate) : "N/A",
          "End Date": r.endDate ? formatDateDisplay(r.endDate) : "N/A",
          "Hours": r.totalHours || (r.totalDays ? r.totalDays * 8 : 0),
          "Status": r.status || "N/A",
          "Reason": r.reason || "N/A",
        }));
        if (data.length === 0) {
          data = [{ "Note": "No time off requests found" }];
        }
        break;
        
      case "employment-summary":
        if (employee) {
          data = [{
            "Employee ID": employee.employeeNumber || employee.id,
            "Name": `${employee.firstName} ${employee.lastName}`,
            "Position": employee.position || "N/A",
            "Department": employee.department || "N/A",
            "Employment Type": employee.employmentType || "N/A",
            "Hire Date": employee.hireDate ? formatDateDisplay(employee.hireDate) : "N/A",
            "Status": employee.status || "N/A",
            "Work Location": employee.city && employee.state ? `${employee.city}, ${employee.state}` : "N/A",
          }];
        } else {
          data = [{ "Note": "Employee data not available" }];
        }
        break;
        
      default:
        data = [{ "Note": "Report data not available" }];
    }
    
    // empty branches inject a single { Note: "..." } placeholder row
    const isEmpty = data.length === 0 || (data.length === 1 && "Note" in data[0]);

    setReportData(data);
    setShowReportPreview(true);
    setRunningReportId(null);

    if (isEmpty) {
      toast({
        title: "No data",
        description: `${report.name} has no records yet`,
      });
    } else {
      toast({
        variant: "success",
        title: "Report ready",
        description: `${report.name} — ${data.length} ${data.length === 1 ? "row" : "rows"}`,
      });
    }

    // wait for the preview to render, then scroll it into view
    requestAnimationFrame(() => {
      reportPreviewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleExport = () => {
    if (currentReport && reportData.length > 0) {
      exportToCSV(reportData, { filename: `${currentReport.id}-${format(new Date(), "yyyy-MM-dd")}` });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">My Reports</h1>
        <p className="text-muted-foreground">View and download your personal reports</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="all">All Reports</TabsTrigger>
          <TabsTrigger value="benefits" className="gap-2">
            <Heart className="h-4 w-4" />
            Benefits
          </TabsTrigger>
          <TabsTrigger value="time" className="gap-2">
            <Clock className="h-4 w-4" />
            Time Off
          </TabsTrigger>
          <TabsTrigger value="personal" className="gap-2">
            <FileText className="h-4 w-4" />
            Personal
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredReports.map((report) => {
              const colors = categoryColors[report.category];
              const Icon = report.icon;
              
              return (
                <Card key={report.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className={`p-2 rounded-lg ${colors.bg}`}>
                        <Icon className={`h-5 w-5 ${colors.icon}`} />
                      </div>
                      <Badge variant="outline" className={colors.text}>
                        {report.category}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg mt-3">{report.name}</CardTitle>
                    <CardDescription>{report.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      className="w-full gap-2"
                      onClick={() => runReport(report)}
                      disabled={isDataLoading || runningReportId !== null}
                    >
                      {runningReportId === report.id ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Generating…
                        </>
                      ) : isDataLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading data…
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4" />
                          Run Report
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {showReportPreview && currentReport && (
        <Card ref={reportPreviewRef} className="scroll-mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  {currentReport.name}
                </CardTitle>
                <CardDescription>
                  Generated on {format(new Date(), "MMMM d, yyyy 'at' h:mm a")}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleExport} className="gap-2">
                  <Download className="h-4 w-4" />
                  Export CSV
                </Button>
                <Button variant="ghost" onClick={() => setShowReportPreview(false)}>
                  Close
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {reportData.length > 0 ? (
              <div className="border rounded-lg overflow-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      {Object.keys(reportData[0]).map((key) => (
                        <th key={key} className="px-4 py-3 text-left text-sm font-medium whitespace-nowrap">
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {reportData.map((row, idx) => (
                      <tr key={idx} className="hover:bg-muted/25">
                        {Object.values(row).map((value: any, vIdx) => (
                          <td key={vIdx} className="px-4 py-3 text-sm whitespace-nowrap">
                            {value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">No data available for this report</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
